/**
 * Charts Service — API client for chart-level operations.
 *
 * All chart endpoints live under /api/v1/charts.
 * Charts are identified globally by `chrt_*` IDs.
 */
import api from './client';
import type {
  Chart,
  ChartWithColumns,
  UpdateChartRequest,
  QueryDataBody,
  ChartDataResponse,
} from './types';

export const chartsService = {
  /**
   * Get chart metadata + dataset view columns.
   */
  getChart: async (chartId: string): Promise<ChartWithColumns> => {
    const res = await api.get<ChartWithColumns>(`/charts/${chartId}`);
    return res;
  },

  /**
   * Create an empty Redis preview for the Add Chart flow.
   * @param datasetViewId — the dataset view to create the preview for
   */
  createChartPreview: async (datasetViewId: string, dashboardId?: string): Promise<{ previewId: string }> => {
    return api.post<{ previewId: string }>('/charts/preview', { datasetViewId, dashboardId });
  },

  /**
   * Create a chart from a Redis preview (Add Chart confirm flow).
   */
  createChartFromPreview: async (dashboardId: string, previewKey: string): Promise<Chart> => {
    return api.post<Chart>('/charts', { dashboardId, previewKey });
  },

  /**
   * Create a chart directly (non-preview flow).
   */
  createChart: async (dashboardId: string, data: Record<string, unknown>): Promise<Chart> => {
    return api.post<Chart>('/charts', { dashboardId, ...data });
  },

  /**
   * Update a chart.
   */
  updateChart: async (chartId: string, data: UpdateChartRequest): Promise<Chart> => {
    return api.put<Chart>(`/charts/${chartId}`, data);
  },

  /**
   * Delete a chart.
   */
  deleteChart: async (chartId: string): Promise<void> => {
    return api.delete<void>(`/charts/${chartId}`);
  },

  /**
   * Duplicate a chart within the same dashboard.
   */
  duplicateChart: async (chartId: string): Promise<Chart> => {
    return api.post<Chart>(`/charts/${chartId}/duplicate`, {});
  },

  /**
   * Execute chart query and get data.
   */
  getChartData: async (chartId: string, options?: QueryDataBody): Promise<ChartDataResponse> => {
    return api.post<ChartDataResponse>(`/charts/${chartId}/data`, options || {});
  },

  /**
   * Update chart preview (proxy to Job Service).
   */
  updateChartPreview: async (body: {
    chartId: string;
    datasetViewId: string;
    dashboardId?: string;
    previewKey?: string;
    fields?: Record<string, unknown>;
    chartStyle?: Record<string, unknown>;
    chartFieldsConfig?: Record<string, unknown>;
    chartType?: string;
    sql?: string;
  }): Promise<{ preview_key: string }> => {
    return api.post<{ preview_key: string }>('/charts/preview-update', body);
  },

  /**
   * Rebuild SQL from a field config.
   */
  rebuildSql: async (body: {
    chart_type: string;
    fields: Record<string, unknown>;
    dataset_view_id: string;
    scatter_mode?: string;
  }): Promise<{ sql: string; field_mappings: Record<string, unknown> }> => {
    return api.post<{ sql: string; field_mappings: Record<string, unknown> }>('/charts/rebuild-sql', body);
  },

  /**
   * Get AI chat history for a chart.
   */
  getChatHistory: async (chartId: string, limit?: number): Promise<{
    messages: Array<{ id: string; role: string; content: string; metadata: unknown; createdAt: string }>;
  }> => {
    const params = limit ? `?limit=${limit}` : '';
    return api.get(`/charts/${chartId}/chat-history${params}`);
  },

  /**
   * Delete AI chat history for a chart.
   */
  deleteChatHistory: async (chartId: string): Promise<{ deleted: boolean }> => {
    return api.delete(`/charts/${chartId}/chat-history`);
  },
};